import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { CheckCircle2, AlertTriangle, RotateCcw, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useFinance } from '@/contexts/FinanceContext';
import type { ImportBatchInput, ReconcileMatchInput } from '@/contexts/FinanceContext';
import { UploadStep } from './steps/UploadStep';
import { MappingStep } from './steps/MappingStep';
import { PreviewStep } from './steps/PreviewStep';
import type {
  CategoryMapping,
  CostCenterMapping,
  ImportResult,
  ImportType,
  LineDecision,
  ParseResult,
} from './types';
import { isOfxResult, typeForImport } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
}

const DEFAULT_COLORS = [
  '#113554',
  '#f37b53',
  '#059669',
  '#d97706',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
];

function pickColor(index: number): string {
  return DEFAULT_COLORS[index % DEFAULT_COLORS.length]!;
}

export function ImportWizard({ open, onClose }: Props) {
  const {
    importBatch,
    reconcileMatches,
    closeCreditCardInvoice,
    getCategoryByName,
    getCostCenterByName,
    filters,
    setFilters,
  } = useFinance();
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [importType, setImportType] = useState<ImportType>('pagar');
  const [accountId, setAccountId] = useState('');
  const [destinationAccountId, setDestinationAccountId] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string | undefined>();
  const [parseResult, setParseResult] = useState<ParseResult | undefined>();
  const [categoryMappings, setCategoryMappings] = useState<CategoryMapping[]>([]);
  const [costCenterMappings, setCostCenterMappings] = useState<CostCenterMapping[]>([]);
  const [lineDecisions, setLineDecisions] = useState<LineDecision[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | undefined>();

  const isOfx = isOfxResult(parseResult);

  // Anos distintos das linhas que entraram no banco (criadas OU reconciliadas).
  // Usado para alertar quando o usuário importa transações de um ano que NÃO é
  // o ano filtrado atualmente — sem esse aviso, a Anny vê "100 importadas" mas
  // a tela de Transações continua vazia porque o filtro está em outro ano.
  const importedYearsOffFilter = useMemo(() => {
    if (!parseResult || !importResult) return [] as number[];
    const created = importResult.transactionsCreated + importResult.transactionsReconciled;
    if (created === 0) return [];
    const years = new Set<number>();
    for (const row of parseResult.rows) {
      const y = row.dueDate.getFullYear();
      if (y !== filters.year) years.add(y);
    }
    return [...years].sort((a, b) => b - a);
  }, [parseResult, importResult, filters.year]);

  /**
   * Após uma importação bem-sucedida, se TODOS os lançamentos criados estão em
   * ano(s) diferente(s) do filtro atual, troca o filtro automaticamente para o
   * ano mais recente importado. Sem esse switch, a Anny via "X criadas" mas a
   * tela de Transações ficava vazia porque o filtro continuava em 2026 e os
   * dados estavam em 2025 — o banner amarelo no fim do wizard não era
   * suficiente porque exigia ela clicar. Aqui forçamos a troca + toast.
   */
  function autoSwitchYearIfNeeded(createdOrReconciled: number) {
    if (createdOrReconciled === 0 || !parseResult) return;
    const years = new Set<number>();
    for (const row of parseResult.rows) years.add(row.dueDate.getFullYear());
    if (years.size === 0 || years.has(filters.year)) return;
    const target = Math.max(...years);
    const { month: _m, ...rest } = filters;
    void _m;
    setFilters({ ...rest, year: target });
    toast.info(
      `Filtro de ano alterado para ${target} para mostrar os lançamentos importados.`,
      { duration: 6000 },
    );
  }

  const resetAll = () => {
    setStep(1);
    setImportType('pagar');
    setAccountId('');
    setFileName(undefined);
    setParseResult(undefined);
    setCategoryMappings([]);
    setCostCenterMappings([]);
    setLineDecisions([]);
    setIsImporting(false);
    setImportResult(undefined);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  /**
   * Após upload, decide qual step abrir:
   * - OFX → preview direto (sem categorias/CCs)
   * - Conta Azul → step 2 (categorias) se houver, senão pula para 3 ou 4
   */
  const handleUploadNext = () => {
    if (!parseResult) return;

    if (isOfx) {
      setCategoryMappings([]);
      setCostCenterMappings([]);
      setLineDecisions([]); // será inicializado no PreviewStep com base nos matches
      setStep(4);
      return;
    }

    // Transferências não têm categoria nem CC — vão direto pro preview.
    if (importType === 'transferir') {
      setCategoryMappings([]);
      setCostCenterMappings([]);
      setStep(4);
      return;
    }

    const txType = typeForImport(importType);
    const cats = parseResult.uniqueCategories.map((name) => {
      const existing = getCategoryByName(name, txType);
      return existing
        ? { name, decision: { kind: 'map' as const, targetId: existing.id } }
        : { name, decision: { kind: 'create' as const } };
    });
    const ccs = parseResult.uniqueCostCenters.map((name) => {
      const existing = getCostCenterByName(name);
      return existing
        ? { name, decision: { kind: 'map' as const, targetId: existing.id } }
        : { name, decision: { kind: 'create' as const } };
    });
    setCategoryMappings(cats);
    setCostCenterMappings(ccs);

    if (cats.length > 0) {
      setStep(2);
    } else if (ccs.length > 0) {
      setStep(3);
    } else {
      setStep(4);
    }
  };

  const handleConfirm = async () => {
    if (!parseResult) return;
    setIsImporting(true);

    let reconciledCount = 0;
    const allErrors: { line?: number; reason: string }[] = [];

    // ---- OFX path ----
    if (isOfx) {
      const matchesPayload: ReconcileMatchInput[] = [];
      const rowsToCreate: typeof parseResult.rows = [];
      const invoiceClosures: Array<{
        cardAccountId: string;
        periodStart: string;
        periodEnd: string;
        paymentDate: string;
      }> = [];

      // Pares paralelos: índices em rowsToCreate apontam pra destino quando há transfer.
      const transferTargetByIndex: Array<string | undefined> = [];

      parseResult.rows.forEach((row, i) => {
        const dec = lineDecisions[i] ?? { kind: 'create' as const };
        if (dec.kind === 'reconcile') {
          matchesPayload.push({
            transactionId: dec.transactionId,
            paymentDate: format(row.paymentDate ?? row.dueDate, 'yyyy-MM-dd'),
            paidAmount: row.amount,
          });
        } else if (dec.kind === 'reconcile_invoice') {
          invoiceClosures.push({
            cardAccountId: dec.cardAccountId,
            periodStart: dec.periodStart,
            periodEnd: dec.periodEnd,
            paymentDate: format(row.paymentDate ?? row.dueDate, 'yyyy-MM-dd'),
          });
        } else if (dec.kind === 'create_transfer') {
          rowsToCreate.push(row);
          transferTargetByIndex.push(dec.destinationAccountId);
        } else {
          rowsToCreate.push(row);
          transferTargetByIndex.push(undefined);
        }
      });

      // 1) Fechamento de faturas (cria transfer + marca N pendentes como pagas)
      for (const closure of invoiceClosures) {
        try {
          const res = await closeCreditCardInvoice({
            cardAccountId: closure.cardAccountId,
            periodStart: closure.periodStart,
            periodEnd: closure.periodEnd,
            paymentAccountId: accountId,
            paymentDate: closure.paymentDate,
          });
          reconciledCount += res.closedCount;
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Erro desconhecido';
          allErrors.push({ reason: `Fechamento de fatura: ${msg}` });
        }
      }

      if (matchesPayload.length > 0) {
        const reconRes = await reconcileMatches(matchesPayload);
        reconciledCount += reconRes.reconciled;
        for (const e of reconRes.errors) {
          allErrors.push({ reason: `Reconciliação: ${e.reason}` });
        }
      }

      const txInput: ImportBatchInput['transactions'] = rowsToCreate.map((row, idx) => {
        const transferTarget = transferTargetByIndex[idx];
        if (transferTarget) {
          // Sentido da transferência: se a linha do OFX é saída (expense),
          // dinheiro sai de accountId para transferTarget. Se é entrada (income),
          // dinheiro vem de transferTarget para accountId.
          const sourceAccountId = row.type === 'income' ? transferTarget : accountId;
          const destAccountId = row.type === 'income' ? accountId : transferTarget;
          return {
            type: 'transfer' as const,
            description: row.description,
            amount: row.amount,
            dueDate: row.dueDate,
            accountId: sourceAccountId,
            destinationAccountId: destAccountId,
            status: row.status,
            ...(row.paymentDate && { paymentDate: row.paymentDate }),
            ...(row.notes && { notes: row.notes }),
            ...(row.externalId && { externalId: row.externalId }),
          };
        }
        return {
          type: row.type ?? 'expense',
          description: row.description,
          amount: row.amount,
          dueDate: row.dueDate,
          accountId,
          status: row.status,
          ...(row.paymentDate && { paymentDate: row.paymentDate }),
          ...(row.notes && { notes: row.notes }),
          ...(row.externalId && { externalId: row.externalId }),
        };
      });

      try {
        const result =
          txInput.length > 0
            ? await importBatch({
                categoriesToCreate: [],
                costCentersToCreate: [],
                transactions: txInput,
              })
            : {
                transactionsCreated: 0,
                transactionsSkippedDuplicate: 0,
                categoriesCreated: 0,
                costCentersCreated: 0,
                errors: [],
              };
        for (const e of result.errors) allErrors.push(e);
        setImportResult({
          transactionsCreated: result.transactionsCreated,
          transactionsReconciled: reconciledCount,
          transactionsSkippedDuplicate: result.transactionsSkippedDuplicate,
          categoriesCreated: 0,
          costCentersCreated: 0,
          errors: allErrors,
        });
        autoSwitchYearIfNeeded(result.transactionsCreated + reconciledCount);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Erro desconhecido';
        setImportResult({
          transactionsCreated: 0,
          transactionsReconciled: reconciledCount,
          transactionsSkippedDuplicate: 0,
          categoriesCreated: 0,
          costCentersCreated: 0,
          errors: [...allErrors, { reason: msg }],
        });
      } finally {
        setIsImporting(false);
        setStep(5);
      }
      return;
    }

    // ---- Conta Azul path (CSV/XLSX) ----
    const txType = typeForImport(importType);

    const catsToCreate = categoryMappings
      .filter((m) => m.decision.kind === 'create')
      .map((m, i) => ({ name: m.name, type: txType, color: pickColor(i) }));

    const ccsToCreate = costCenterMappings
      .filter((m) => m.decision.kind === 'create')
      .map((m, i) => ({ name: m.name, color: pickColor(i) }));

    const txInput: ImportBatchInput['transactions'] = parseResult.rows.map((row) => {
      const catMapping = row.categoryName
        ? categoryMappings.find((m) => m.name === row.categoryName)
        : undefined;
      const ccMapping = row.costCenterName
        ? costCenterMappings.find((m) => m.name === row.costCenterName)
        : undefined;

      const base = {
        type: txType,
        description: row.description,
        amount: row.amount,
        dueDate: row.dueDate,
        accountId,
        status: row.status,
        ...(row.paymentDate && { paymentDate: row.paymentDate }),
        ...(row.notes && { notes: row.notes }),
        ...(txType === 'transfer' && destinationAccountId
          ? { destinationAccountId }
          : {}),
      };

      // Para transferências, ignora categoria e centro de custo (não fazem sentido em transfer).
      if (txType === 'transfer') {
        return base;
      }

      let categoryPart = {};
      if (catMapping?.decision.kind === 'create' && row.categoryName) {
        categoryPart = { categoryName: row.categoryName };
      } else if (catMapping?.decision.kind === 'map') {
        categoryPart = { existingCategoryId: catMapping.decision.targetId };
      }

      let ccPart = {};
      if (ccMapping?.decision.kind === 'create' && row.costCenterName) {
        ccPart = { costCenterName: row.costCenterName };
      } else if (ccMapping?.decision.kind === 'map') {
        ccPart = { existingCostCenterId: ccMapping.decision.targetId };
      }

      return { ...base, ...categoryPart, ...ccPart };
    });

    try {
      const result = await importBatch({
        categoriesToCreate: catsToCreate,
        costCentersToCreate: ccsToCreate,
        transactions: txInput,
      });
      setImportResult({
        transactionsCreated: result.transactionsCreated,
        transactionsReconciled: 0,
        transactionsSkippedDuplicate: result.transactionsSkippedDuplicate,
        categoriesCreated: result.categoriesCreated,
        costCentersCreated: result.costCentersCreated,
        errors: result.errors,
      });
      autoSwitchYearIfNeeded(result.transactionsCreated);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro desconhecido';
      setImportResult({
        transactionsCreated: 0,
        transactionsReconciled: 0,
        transactionsSkippedDuplicate: 0,
        categoriesCreated: 0,
        costCentersCreated: 0,
        errors: [{ line: 0, reason: msg }],
      });
    } finally {
      setIsImporting(false);
      setStep(5);
    }
  };

  // Navegação inteligente de "Voltar" no preview
  const previewBackTarget = (): 1 | 2 | 3 => {
    if (isOfx) return 1;
    if (costCenterMappings.length > 0) return 3;
    if (categoryMappings.length > 0) return 2;
    return 1;
  };

  // Navegação no step 2 → 3 ou 4
  const fromCategoriesNext = () => {
    if (costCenterMappings.length > 0) setStep(3);
    else setStep(4);
  };

  const title = useMemo(() => {
    if (isOfx) {
      const labels: Record<number, string> = { 1: 'Arquivo', 4: 'Preview', 5: 'Resultado' };
      const label = labels[step] ?? '';
      return `Importar extrato OFX — ${label}`;
    }
    const base =
      importType === 'fatura-cartao' ? 'Importar fatura de cartão' : 'Importar do Conta Azul';
    const labels = ['Arquivo', 'Categorias', 'Centros de custo', 'Preview', 'Resultado'];
    return step <= 5 ? `${base} — ${labels[step - 1]} (${step}/5)` : base;
  }, [step, isOfx, importType]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">{title}</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <UploadStep
            importType={importType}
            accountId={accountId}
            {...(destinationAccountId !== undefined && { destinationAccountId })}
            onUpdate={(patch) => {
              if (patch.importType !== undefined) {
                setImportType(patch.importType);
                if (patch.importType !== 'transferir') setDestinationAccountId(undefined);
              }
              if (patch.accountId !== undefined) setAccountId(patch.accountId);
              if (patch.destinationAccountId !== undefined)
                setDestinationAccountId(patch.destinationAccountId);
              if (patch.fileName !== undefined) setFileName(patch.fileName);
              if (patch.parseResult !== undefined) setParseResult(patch.parseResult);
            }}
            onNext={handleUploadNext}
          />
        )}

        {step === 2 && parseResult && (
          <MappingStep
            mappingType="category"
            categoryType={typeForImport(importType)}
            mappings={categoryMappings}
            onUpdate={(m) => setCategoryMappings(m as CategoryMapping[])}
            onBack={() => setStep(1)}
            onNext={fromCategoriesNext}
          />
        )}

        {step === 3 && parseResult && (
          <MappingStep
            mappingType="costCenter"
            mappings={costCenterMappings}
            onUpdate={(m) => setCostCenterMappings(m as CostCenterMapping[])}
            onBack={() => setStep(categoryMappings.length > 0 ? 2 : 1)}
            onNext={() => setStep(4)}
          />
        )}

        {step === 4 && parseResult && (
          <PreviewStep
            rows={parseResult.rows}
            categoryMappings={categoryMappings}
            costCenterMappings={costCenterMappings}
            isImporting={isImporting}
            isOfx={isOfx}
            accountId={accountId}
            lineDecisions={lineDecisions}
            onLineDecisionsChange={setLineDecisions}
            onBack={() => setStep(previewBackTarget())}
            onConfirm={handleConfirm}
          />
        )}

        {step === 5 && importResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {importResult.errors.length === 0 ? (
                <>
                  <CheckCircle2 size={32} className="text-income" />
                  <div>
                    <h3 className="font-brand text-base font-semibold">Importação concluída</h3>
                    <p className="text-sm text-muted-foreground">
                      {fileName ? `Arquivo: ${fileName}` : 'Todos os lançamentos foram importados.'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle size={32} className="text-warning" />
                  <div>
                    <h3 className="font-brand text-base font-semibold">Importação com avisos</h3>
                    <p className="text-sm text-muted-foreground">
                      Alguns itens falharam — detalhes abaixo.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <div className="rounded-lg border border-border bg-white p-3 text-center">
                <div className="text-2xl font-bold text-primary">
                  {importResult.transactionsCreated}
                </div>
                <div className="text-xs text-muted-foreground">Novas</div>
              </div>
              <div className="rounded-lg border border-border bg-white p-3 text-center">
                <div className="inline-flex items-center gap-1 text-2xl font-bold text-amber-600">
                  <RotateCcw size={18} />
                  {importResult.transactionsReconciled}
                </div>
                <div className="text-xs text-muted-foreground">Reconciliadas</div>
              </div>
              <div className="rounded-lg border border-border bg-white p-3 text-center">
                <div
                  className={`text-2xl font-bold ${importResult.transactionsSkippedDuplicate > 0 ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}
                  title="Já existiam no sistema (mesmo FITID/external_id) — foram ignoradas para evitar duplicação"
                >
                  {importResult.transactionsSkippedDuplicate}
                </div>
                <div className="text-xs text-muted-foreground">Já existiam</div>
              </div>
              <div className="rounded-lg border border-border bg-white p-3 text-center">
                <div className="text-2xl font-bold text-income">
                  {importResult.categoriesCreated}
                </div>
                <div className="text-xs text-muted-foreground">Categorias novas</div>
              </div>
              <div className="rounded-lg border border-border bg-white p-3 text-center">
                <div className="text-2xl font-bold text-income">
                  {importResult.costCentersCreated}
                </div>
                <div className="text-xs text-muted-foreground">Centros de custo</div>
              </div>
            </div>

            {importedYearsOffFilter.length > 0 && (
              <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-2 text-amber-800">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">
                      Os lançamentos importados não aparecem na tela atual.
                    </p>
                    <p className="text-xs text-amber-700">
                      Você está vendo o ano <strong>{filters.year}</strong>, mas os dados estão em{' '}
                      <strong>
                        {importedYearsOffFilter.length === 1
                          ? importedYearsOffFilter[0]
                          : importedYearsOffFilter.join(', ')}
                      </strong>
                      . Mude o filtro de ano para visualizá-los.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-amber-400 bg-white text-amber-800 hover:bg-amber-100"
                  onClick={() => {
                    const targetYear = importedYearsOffFilter[0];
                    if (targetYear !== undefined) {
                      // Trocar o ano sozinho não basta: o filtro de mês default
                      // é o mês corrente, então a Anny ainda veria só junho/2025
                      // e jan-mai ficariam invisíveis. Remove a chave `month`
                      // para mostrar o ano inteiro.
                      const { month: _month, ...rest } = filters;
                      void _month;
                      setFilters({ ...rest, year: targetYear });
                    }
                  }}
                >
                  <Calendar size={14} className="mr-1.5" />
                  Ver {importedYearsOffFilter[0]}
                </Button>
              </div>
            )}

            {importResult.errors.length > 0 && (
              <div className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs">
                <p className="mb-1 font-semibold text-warning">Erros:</p>
                <ul className="max-h-32 space-y-0.5 overflow-y-auto text-muted-foreground">
                  {importResult.errors.map((e, i) => (
                    <li key={i}>
                      {e.line ? `Linha ${e.line}: ` : ''}
                      {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={handleClose}
                className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
              >
                Fechar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
